import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import type { Application } from '@cef-ai/wallet-api-client';
import { useWallet } from '~/wallet';
import { AppPage } from '~/ui/AppPage';
import { Card, Chip, EmptyState, ErrorText, Loading } from '~/ui/primitives';

export const Applications = observer(() => {
  const { identity, applicationsStore } = useWallet();
  const [apps, setApps] = useState<Application[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!identity.isAuthenticated) return;
    applicationsStore
      .listAll()
      .then(setApps)
      .catch((e) => setError(e?.message ?? 'load failed'));
  }, [applicationsStore, identity.isAuthenticated]);

  if (!identity.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AppPage title="Applications">
      {error && <ErrorText>{error}</ErrorText>}
      {!error && apps === null && (
        <Card>
          <Loading />
        </Card>
      )}
      {apps && apps.length === 0 && (
        <Card>
          <EmptyState title="No apps connected yet" hint="Apps you grant access to will appear here." />
        </Card>
      )}
      {apps && apps.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {apps.map((app) => (
            <Link key={app.appId} to={`/applications/${app.appId}`} style={{ textDecoration: 'none' }}>
              <Card
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 'var(--space-4)',
                }}
              >
                <span style={{ font: '500 16px/1.3 var(--font-sans)', color: 'var(--text-primary)' }}>{app.appId}</span>
                <Chip tone="neutral">
                  {Object.keys(app.permissions).length} permission{Object.keys(app.permissions).length === 1 ? '' : 's'}
                </Chip>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </AppPage>
  );
});
